import { Injectable } from '@angular/core';
import { DwLoggingService } from '@devwareapps/devware-cap';

@Injectable({
  providedIn: 'root'
})
export class StripeDiagnosticsService {
  private handlersInstalled = false;

  constructor(private dwLoggingService: DwLoggingService) {
  }

  installGlobalHandlers() {
    if (this.handlersInstalled || typeof window === 'undefined') {
      return;
    }

    window.addEventListener('error', this.handleWindowError as EventListener);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    this.handlersInstalled = true;
  }

  logError(context: string, error: any, details?: any) {
    const serializedError = this.serializeError(error);
    const payload = {
      error: serializedError,
      details
    };
    const message = this.buildLogMessage(context, serializedError, details);

    try {
      this.dwLoggingService.logError(message, error, null, true);
    } catch (loggingError) {
      console.error('Stripe diagnostics logging failed', loggingError);
    }

    console.error(context, payload);
  }

  private handleWindowError = (event: ErrorEvent | Event) => {
    const targetSource = (event as any)?.target?.src;
    const details = {
      filename: (event as ErrorEvent).filename,
      lineno: (event as ErrorEvent).lineno,
      colno: (event as ErrorEvent).colno,
      source: targetSource
    };

    if (this.isStripeWindowError(event as ErrorEvent, details)) {
      this.logError('Stripe.js window error', (event as ErrorEvent).error ?? (event as ErrorEvent).message ?? 'Stripe script load error', details);
    }
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (this.isStripeRelated(event.reason)) {
      this.logError('Stripe.js unhandled promise rejection', event.reason);
    }
  };

  private isStripeWindowError(event: ErrorEvent, details?: any) {
    const filename = `${details?.filename ?? event?.filename ?? ''}`.toLowerCase();
    const source = `${details?.source ?? ''}`.toLowerCase();
    if (filename.indexOf('js.stripe.com') > -1 || source.indexOf('js.stripe.com') > -1) {
      return true;
    }

    return this.isStripeRelated(event?.error ?? event?.message);
  }

  private isStripeRelated(value: any) {
    if (value == undefined) {
      return false;
    }

    const text = typeof value == 'string'
      ? value
      : [
        value?.name,
        value?.message,
        value?.stack,
        value?.type,
        value?.code
      ].filter(v => `${v ?? ''}`.length > 0).join(' ');

    const normalized = text.toLowerCase();
    return normalized.indexOf('stripe') > -1
      || normalized.indexOf('payment element') > -1
      || normalized.indexOf('confirmsetup') > -1
      || normalized.indexOf('elements.create') > -1
      || normalized.indexOf('integrationerror') > -1;
  }

  private serializeError(error: any) {
    if (error == undefined) {
      return null;
    }

    if (typeof error == 'string') {
      return { message: error };
    }

    return {
      name: error?.name,
      message: error?.message,
      type: error?.type,
      code: error?.code,
      declineCode: error?.decline_code ?? error?.declineCode,
      stack: error?.stack
    };
  }

  private buildLogMessage(context: string, error: any, details?: any) {
    const parts = [context];
    const errorDetails = this.flattenDetails(error, 'error');
    const contextDetails = this.flattenDetails(details, 'details');

    if (errorDetails.length > 0) {
      parts.push(errorDetails.join(', '));
    }

    if (contextDetails.length > 0) {
      parts.push(contextDetails.join(', '));
    }

    return parts.join(' | ');
  }

  private flattenDetails(value: any, prefix?: string) {
    if (value == undefined) {
      return [];
    }

    if (typeof value != 'object' || value instanceof Date) {
      return [`${prefix ?? 'value'}=${this.formatValue(value)}`];
    }

    return Object.keys(value)
      .filter(key => value[key] != undefined && value[key] !== '')
      .map(key => `${prefix ? `${prefix}.` : ''}${key}=${this.formatValue(value[key])}`);
  }

  private formatValue(value: any) {
    if (value == undefined) {
      return '';
    }

    if (typeof value == 'string') {
      return value;
    }

    if (typeof value == 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return `${value}`;
      }
    }

    return `${value}`;
  }
}
